import { LengthUnit } from '@fulcrum/lib';
import { t } from '@trimble/i18n';
import {
  Measurement,
  MeasurementInfoButtonConfig,
  PointMeasurementType,
} from '@trimble/web3d-providers';
import { createMemo, Show, type JSX } from 'solid-js';
import { LengthFormatter } from '@trimble/utilities';
import {
  MeasurementProperty,
  ResultRowProps,
} from '@trimble/measurements-shared';
import { FormattingSettings } from '@trimble/types';

export interface PointMeasurementDetailsProps {
  measurement: Measurement | undefined;
  baseLengthUnit: LengthUnit;
  formattingSettings: FormattingSettings;
  infoButtonConfig?: MeasurementInfoButtonConfig;
}

export const PointMeasurementDetails = (
  props: PointMeasurementDetailsProps
): JSX.Element | null => {
  const measurementData = createMemo((): PointMeasurementType | null => {
    if (!props.measurement) return null;
    return props.measurement as PointMeasurementType;
  });

  const horizontalData = (data: PointMeasurementType): ResultRowProps[] => [
    {
      cells: [
        {
          label: t('measurements.point.x'),
          value: LengthFormatter.toDisplay({
            measuredValue: data.x,
            currentUnits: props.baseLengthUnit,
            settings: props.formattingSettings,
            useMeasurementSettings: true,
          }),
          testId: 'point-x',
          valueTestId: 'point-x-value',
        },
        {
          label: t('measurements.point.y'),
          value: LengthFormatter.toDisplay({
            measuredValue: data.y,
            currentUnits: props.baseLengthUnit,
            settings: props.formattingSettings,
            useMeasurementSettings: true,
          }),
          testId: 'point-y',
          valueTestId: 'point-y-value',
        },
      ],
      testId: 'horizontal-values',
    },
  ];

  const elevationData = (data: PointMeasurementType): ResultRowProps[] => [
    {
      cells: [
        {
          label: t('measurements.point.z'),
          value: LengthFormatter.toDisplay({
            measuredValue: data.z,
            currentUnits: props.baseLengthUnit,
            settings: props.formattingSettings,
            useMeasurementSettings: true,
          }),
          testId: 'point-z',
          valueTestId: 'point-z-value',
        },
      ],
      testId: 'elevation-values',
    },
  ];

  return (
    <Show when={measurementData()}>
      {(data) => (
        <div
          class="flex flex-col gap-3 p-3"
          data-testid="point-measurement-details"
        >
          <MeasurementProperty
            icon="tc-icon-3D-group"
            title={t('measurements.point.horizontal')}
            resultRows={horizontalData(data())}
            infoButtonConfig={props.infoButtonConfig}
          />
          <MeasurementProperty
            icon="tc-icon-elevation"
            title={t('measurements.point.elevation')}
            resultRows={elevationData(data())}
            infoButtonConfig={props.infoButtonConfig}
          />
        </div>
      )}
    </Show>
  );
};
